/* The combobox pattern keeps focus in the input and points at options via aria-activedescendant, so options
   are intentionally not focusable and have no own key handlers; the a11y lint rules below assume the opposite. */
/* oxlint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus, jsx-a11y/prefer-tag-over-role */
import { CornerDownLeft, Search, type LucideIcon } from 'lucide-react'
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { cx } from './cx'
import { useModal } from './Dialog'
import { Kbd } from './misc'
import { Spinner } from './Spinner'

export type Command = {
  id: string
  label: string
  group: string
  icon?: LucideIcon
  /** Extra search terms ("Aufstellung Startelf"). */
  keywords?: string[]
  /** Right-aligned context ("Spieler · ZM"). */
  hint?: string
  onSelect: () => void
}

type Search = (query: string, signal: AbortSignal) => Promise<Command[]>

type CommandPaletteProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  commands: Command[]
  /** Remote results (players, matches …) for the query; called debounced, aborted when superseded. */
  search?: Search
  placeholder?: string
}

const fold = (s: string) => s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()

function matches(c: Command, terms: string[]) {
  const hay = fold([c.label, c.group, ...(c.keywords ?? [])].join(' '))
  return terms.every((t) => hay.includes(t))
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

/** Shortcut label for buttons that open the palette ("⌘K" / "Strg K"). */
export const commandShortcut = isMac ? '⌘K' : 'Strg K'

/** ⌘K / Ctrl+K: search and jump. Combobox + listbox pattern with aria-activedescendant. */
export function CommandPalette({ open, onOpenChange, commands, search, placeholder = 'Suchen oder Befehl eingeben …' }: CommandPaletteProps) {
  const ref = useModal(open, () => onOpenChange(false))

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        onOpenChange(!open)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onOpenChange])

  return (
    <dialog
      ref={ref}
      aria-label="Befehle und Suche"
      className="m-0 mx-auto mt-[min(12vh,120px)] max-h-none w-[calc(100%-1.5rem)] max-w-[600px] animate-pop overflow-hidden rounded-card border border-line bg-surface p-0 text-ink shadow-float"
    >
      {/* Mounted only while open, so query and selection start fresh each time. */}
      {open && (
        <Palette
          commands={commands}
          placeholder={placeholder}
          onRun={(c) => {
            onOpenChange(false)
            c.onSelect()
          }}
          {...(search ? { search } : {})}
        />
      )}
    </dialog>
  )
}

type PaletteProps = { commands: Command[]; search?: Search; placeholder: string; onRun: (c: Command) => void }

function Palette({ commands, search, placeholder, onRun }: PaletteProps) {
  const id = useId()
  const listRef = useRef<HTMLDivElement>(null)
  const [query, setQuery] = useState('')
  const [remote, setRemote] = useState<{ query: string; results: Command[] }>({ query: '', results: [] })
  const [active, setActive] = useState(0)
  const q = query.trim()
  const wantsRemote = Boolean(search) && q.length >= 2
  const busy = wantsRemote && remote.query !== q

  useEffect(() => {
    if (!search || !wantsRemote) return
    const ctrl = new AbortController()
    const t = setTimeout(() => {
      search(q, ctrl.signal)
        .catch(() => [])
        .then((results) => !ctrl.signal.aborted && setRemote({ query: q, results }))
    }, 150)
    return () => {
      clearTimeout(t)
      ctrl.abort()
    }
  }, [q, search, wantsRemote])

  const groups = useMemo(() => {
    const terms = fold(q).split(/\s+/).filter(Boolean)
    const local = terms.length ? commands.filter((c) => matches(c, terms)) : commands
    const seen = new Set(local.map((c) => c.id))
    const extra = wantsRemote && remote.query === q ? remote.results.filter((c) => !seen.has(c.id)) : []
    const map = new Map<string, Command[]>()
    for (const c of [...local, ...extra]) map.set(c.group, [...(map.get(c.group) ?? []), c])
    return [...map.entries()]
  }, [commands, remote, q, wantsRemote])

  const flat = groups.flatMap(([, cs]) => cs)
  const current = Math.min(active, Math.max(0, flat.length - 1))

  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${current}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [current])

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      const d = e.key === 'ArrowDown' ? 1 : -1
      setActive((current + d + flat.length) % Math.max(1, flat.length))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const c = flat[current]
      if (c) onRun(c)
    }
  }

  let index = -1
  return (
    <div className="flex max-h-[min(70dvh,560px)] flex-col">
      <div className="flex items-center gap-3 border-b border-line px-4">
        {busy ? (
          <span className="text-ink-3">
            <Spinner size="sm" />
          </span>
        ) : (
          <Search aria-hidden="true" className="size-[18px] shrink-0 text-ink-3" />
        )}
        <input
          role="combobox"
          aria-expanded="true"
          aria-controls={`${id}-list`}
          aria-activedescendant={flat[current] ? `${id}-opt-${current}` : undefined}
          aria-autocomplete="list"
          aria-label="Suchen"
          data-autofocus
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setActive(0)
          }}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          spellCheck={false}
          className="h-14 min-w-0 flex-1 bg-transparent text-body pointer-coarse:text-base text-ink outline-none placeholder:text-ink-3 focus-visible:outline-none"
        />
        <Kbd>Esc</Kbd>
      </div>
      <div ref={listRef} id={`${id}-list`} role="listbox" aria-label="Ergebnisse" className="min-h-0 flex-1 overflow-y-auto p-2">
        {flat.length === 0 ? (
          <p role="status" className="px-3 py-10 text-center text-small text-ink-3">
            {busy ? 'Suche läuft …' : `Nichts gefunden für „${q}“.`}
          </p>
        ) : (
          groups.map(([group, cs]) => (
            <div key={group} role="group" aria-labelledby={`${id}-g-${group}`} className="not-first:mt-2">
              <div id={`${id}-g-${group}`} className="px-3 pt-2 pb-1 text-label font-medium text-ink-3">
                {group}
              </div>
              {cs.map((c) => {
                index++
                const i = index
                const Icon = c.icon
                const selected = i === current
                return (
                  <div
                    key={c.id}
                    id={`${id}-opt-${i}`}
                    data-index={i}
                    role="option"
                    aria-selected={selected}
                    onPointerMove={() => active !== i && setActive(i)}
                    onClick={() => onRun(c)}
                    className={cx(
                      'flex h-11 cursor-pointer items-center gap-3 rounded-control px-3 text-body select-none',
                      selected ? 'bg-bg-hover text-ink' : 'text-ink-2',
                    )}
                  >
                    {Icon && <Icon aria-hidden="true" className={cx('size-4 shrink-0', selected ? 'text-ink' : 'text-ink-3')} />}
                    <span className="min-w-0 flex-1 truncate">{c.label}</span>
                    {c.hint && <span className="shrink-0 text-small text-ink-3">{c.hint}</span>}
                    {selected && <CornerDownLeft aria-hidden="true" className="size-3.5 shrink-0 text-ink-3" />}
                  </div>
                )
              })}
            </div>
          ))
        )}
      </div>
      <div className="hidden items-center gap-4 border-t border-line px-4 py-2.5 text-label text-ink-3 md:flex">
        <span className="inline-flex items-center gap-1.5">
          <Kbd>↑</Kbd>
          <Kbd>↓</Kbd> auswählen
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Kbd>↵</Kbd> öffnen
        </span>
      </div>
    </div>
  )
}
